import { useMemo, useState, useEffect, useRef } from 'react'
import {
  Box,
  Button,
  Checkbox,
  CheckboxGroup,
  Divider,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Grid,
  GridItem,
  HStack,
  IconButton,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  NumberDecrementStepper,
  NumberIncrementStepper,
  NumberInput,
  NumberInputField,
  NumberInputStepper,
  Popover,
  PopoverTrigger,
  PopoverContent,
  PopoverBody,
  Portal,
  Select,
  Text,
  Textarea,
  VStack,
  Wrap,
  WrapItem,
} from '@chakra-ui/react'
import { X, ExternalLink, Trash2 } from 'lucide-react'
import DateTimePickerPopover from '@/components/client/DateTimePickerPopover'

type Option = { label: string; value: string; parent_id?: string }

/** Ticket data from API (GET /support/my-request/:id) for prefilling edit form */
export type TicketInitialData = {
  id?: number
  service_type_id?: number
  service_parent_id?: number
  category_id?: number
  category_ids?: number[]
  description?: string
  requested_quantity?: number
  preferred_schedule_at?: string | null
  contact_number?: string
  contact_name?: string
  contact_email?: string
  preferred_contact_at?: string | null
  attachment_metadata?: Array<{ file_name: string; path: string; file_url?: string }>
}

export type CreateTicketPayload = {
  service: string
  category_ids: string[]
  description: string
  devicesAffected: number
  dispatchDate: string
  dispatchTime: string
  contactNumber: string
  contactPerson: string
  email: string
  preferredContactDate: string
  preferredContactTime: string
  attachments: File[]
  existingAttachments?: Array<{ file_name: string; path: string; file_url?: string }>
  submitRequest: boolean
}

type Props = {
  isOpen: boolean
  onClose: () => void
  isSubmitting?: boolean
  onSubmit: (payload: CreateTicketPayload) => Promise<void> | void
  /** When true, show loading in modal body (e.g. fetching ticket for edit). */
  isLoadingEdit?: boolean

  serviceOptions?: Option[] // Child service types (actual selectable types)
  serviceParentOptions?: Option[] // Parent service categories (Incident Request, Service Request)
  categoryOptions?: Option[]

  defaultService?: string
  defaultCategory?: string
  /** When set, form is in edit mode: prefilled and submit updates instead of create */
  initialData?: TicketInitialData | null
  /** When set (e.g. from parent when Edit clicked), show edit title even before initialData loads */
  editingId?: number | null
}

const DEFAULT_SERVICES: Option[] = [
  { label: 'Breakfix', value: 'breakfix' },
  { label: 'Preventive Maintenance', value: 'pm' },
  { label: 'Installation', value: 'installation' },
]

function todayISO() {
  const d = new Date()
  const yyyy = d.getFullYear()
  const mm = String(d.getMonth() + 1).padStart(2, '0')
  const dd = String(d.getDate()).padStart(2, '0')
  return `${yyyy}-${mm}-${dd}`
}

/** Parse backend datetime "Y-m-d H:i:s" or ISO into { date, time } for DateTimePickerPopover */
function parseDateTime(value: string | null | undefined): { date?: Date; time?: string } {
  if (!value) return {}
  const d = new Date(value)
  if (isNaN(d.getTime())) return {}
  const date = d
  const hours = d.getHours()
  const minutes = d.getMinutes()
  const time = `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`
  return { date, time }
}

function dateToISO(date: Date): string {
  return date.toISOString().slice(0, 10)
}

export function CreateTicketModal({
  isOpen,
  onClose,
  onSubmit,
  isSubmitting = false,
  serviceOptions,
  serviceParentOptions,
  categoryOptions,
  defaultService,
  defaultCategory,
  initialData,
  editingId,
  isLoadingEdit = false,
}: Props) {
  const serviceTypes = serviceOptions ?? [] // Child service types
  const serviceParents = serviceParentOptions ?? [] // Parent service categories
  const isEditMode = Boolean(editingId != null || initialData)

  // Form state
  const [serviceParent, setServiceParent] = useState<string>('')
  const [serviceType, setServiceType] = useState(
    defaultService ?? '',
  )
  const [description, setDescription] = useState('')
  const [devicesAffected, setDevicesAffected] = useState<number>(1)

  const [categoryIds, setCategoryIds] = useState<string[]>(
    defaultCategory ? [defaultCategory] : [],
  )

  const [dispatchDate, setDispatchDate] = useState('')
  const [dispatchTime, setDispatchTime] = useState('')

  const [contactNumber, setContactNumber] = useState('')
  const [contactPerson, setContactPerson] = useState('')
  const [email, setEmail] = useState('')

  const [preferredContactDate, setPreferredContactDate] = useState(todayISO())
  const [preferredContactTime, setPreferredContactTime] = useState('10:00')

  const [attachments, setAttachments] = useState<File[]>([])
  const [existingAttachments, setExistingAttachments] = useState<Array<{ file_name: string; path: string; file_url?: string }>>([])
  const [isDragging, setIsDragging] = useState(false)
  const [dispatch, setDispatch] = useState<{ date?: Date; time?: string }>({})
  const [touched, setTouched] = useState<Record<string, boolean>>({})

  const [dispatchDT, setDispatchDT] = useState<{ date?: Date; time?: string }>({})
  const [contactDate, setContactDate] = useState<{ date?: Date; time?: string }>({})

  // Filter child service types based on selected parent
  const filteredServiceTypes = useMemo(() => {
    if (!serviceParent || !serviceTypes.length) return []
    // Filter child types that belong to the selected parent
    return serviceTypes.filter((st) => {
      const stWithParent = st as Option & { parent_id?: string }
      return stWithParent.parent_id && String(stWithParent.parent_id) === String(serviceParent)
    })
  }, [serviceParent, serviceTypes])

  // When selected service (parent) has no child types, Type of Service is not required
  const hasChildTypes = filteredServiceTypes.length > 0
  const effectiveServiceId = hasChildTypes ? serviceType : serviceParent

  // When parent changes, reset child selection (unless we're prefilling from initialData)
  // Use a ref to track if we're in the initial prefill phase
  const isPrefillingRef = useRef(false)
  
  useEffect(() => {
    if (serviceParent && !isPrefillingRef.current) {
      setServiceType('')
    }
  }, [serviceParent])

  // Prefill form when opening in edit mode; reset when opening in create mode
  // Lock body scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      // Save current body overflow style
      const originalStyle = window.getComputedStyle(document.body).overflow
      // Lock body scroll
      document.body.style.overflow = 'hidden'
      // Restore on cleanup
      return () => {
        document.body.style.overflow = originalStyle
      }
    }
  }, [isOpen])

  useEffect(() => {
    if (!isOpen) {
      isPrefillingRef.current = false
      return
    }
    if (initialData) {
      isPrefillingRef.current = true
      // Set parent service first (so filteredServiceTypes is populated)
      const serviceParentId = String(initialData.service_parent_id ?? '')
      const serviceTypeId = String(initialData.service_type_id ?? '')
      // When ticket has parent-only service (e.g. Inquiry), API returns service_parent_id null; use service_type_id as Service dropdown value
      const effectiveParentId = serviceParentId || serviceTypeId
      if (effectiveParentId) {
        setServiceParent(effectiveParentId)
      }
      // Set service type (child) from initialData; for parent-only it's the same id
      if (serviceTypeId) {
        setTimeout(() => {
          setServiceType(serviceTypeId)
          isPrefillingRef.current = false
        }, 10)
      } else {
        isPrefillingRef.current = false
      }
      const ids = initialData.category_ids?.length
        ? initialData.category_ids.map(String)
        : (initialData.category_id != null ? [String(initialData.category_id)] : [])
      setCategoryIds(ids)
      setDescription(initialData.description ?? '')
      setDevicesAffected(initialData.requested_quantity ?? 1)
      setContactNumber(initialData.contact_number ?? '')
      setContactPerson(initialData.contact_name ?? '')
      setEmail(initialData.contact_email ?? '')
      const dispatchParsed = parseDateTime(initialData.preferred_schedule_at)
      const hasDispatch = dispatchParsed.date && dispatchParsed.time
      if (hasDispatch) {
        setDispatchDT(dispatchParsed)
        setDispatchDate(dateToISO(dispatchParsed.date!))
        setDispatchTime(dispatchParsed.time!)
      } else {
        const defaultDate = new Date()
        setDispatchDT({ date: defaultDate, time: '10:00' })
        setDispatchDate(dateToISO(defaultDate))
        setDispatchTime('10:00')
      }
      const contactParsed = parseDateTime(initialData.preferred_contact_at)
      const hasContact = contactParsed.date && contactParsed.time
      if (hasContact) {
        setContactDate(contactParsed)
        setPreferredContactDate(dateToISO(contactParsed.date!))
        setPreferredContactTime(contactParsed.time!)
      } else {
        const defaultDate = new Date()
        setContactDate({ date: defaultDate, time: '10:00' })
        setPreferredContactDate(dateToISO(defaultDate))
        setPreferredContactTime('10:00')
      }
      setAttachments([])
      // Set existing attachments from initialData
      setExistingAttachments(initialData.attachment_metadata || [])
    } else {
      setServiceParent('')
      setServiceType('')
      setCategoryIds(defaultCategory ? [defaultCategory] : [])
      setDescription('')
      setDevicesAffected(1)
      setDispatchDate('')
      setDispatchTime('')
      setDispatchDT({})
      setContactNumber('')
      setContactPerson('')
      setEmail('')
      setPreferredContactDate(todayISO())
      setPreferredContactTime('10:00')
      setContactDate({})
      setAttachments([])
      setExistingAttachments([])
    }
    setTouched({})
  }, [isOpen, initialData, serviceTypes, serviceParentOptions])

  // Manage object URLs for image previews
  const previewUrls = useMemo(() => {
    return attachments
      .filter(file => file.type.startsWith('image/'))
      .map(file => URL.createObjectURL(file))
  }, [attachments])

  // Cleanup object URLs when component unmounts or attachments change
  useEffect(() => {
    return () => {
      previewUrls.forEach(url => URL.revokeObjectURL(url))
    }
  }, [previewUrls])

  // Validate only fields that exist in the form
  const errors = useMemo(() => {
    const e: Record<string, string> = {}

    if (!serviceParent) e.serviceParent = 'Service is required'
    if (hasChildTypes && !serviceType) e.serviceType = 'Type of Service is required'
    // Categories are now optional - removed validation
    if (!description.trim()) e.description = 'Issue Description is required'

    if (!contactNumber.trim()) e.contactNumber = 'Contact Number is required'
    if (!contactPerson.trim()) e.contactPerson = 'Contact Person is required'

    if (!email.trim()) e.email = 'Email Address is required'
    else if (!/^\S+@\S+\.\S+$/.test(email))
      e.email = 'Enter a valid email address'

    if (!devicesAffected || devicesAffected < 1)
      e.devicesAffected = 'Must be at least 1'

    const tooLarge = attachments.find((f) => f.size > 5 * 1024 * 1024)
    if (tooLarge) e.attachments = 'Each file must be 5MB or less'

    return e
  }, [
    serviceParent,
    serviceType,
    hasChildTypes,
    categoryIds,
    description,
    contactNumber,
    contactPerson,
    email,
    devicesAffected,
    attachments,
  ])

  const hasErrors = Object.keys(errors).length > 0

  function markTouched(key: string) {
    setTouched((prev) => ({ ...prev, [key]: true }))
  }

  function resetForm() {
    setServiceParent('')
    setServiceType(defaultService ?? '')
    setCategoryIds(defaultCategory ? [defaultCategory] : [])
    setDescription('')
    setDevicesAffected(1)
    setDispatchDate('')
    setDispatchTime('')
    setContactNumber('')
    setContactPerson('')
    setEmail('')
    setPreferredContactDate(todayISO())
    setPreferredContactTime('10:00')
    setAttachments([])
    setExistingAttachments([])
    setTouched({})
  }

  async function handleSubmit(submitRequest: boolean) {
    setTouched({
      serviceParent: true,
      serviceType: true,
      category: true,
      description: true,
      dispatchDate: true,
      dispatchTime: true,
      contactNumber: true,
      contactPerson: true,
      email: true,
      preferredContactDate: true,
      preferredContactTime: true,
      devicesAffected: true,
      attachments: true,
    })

    if (hasErrors) return

    try {
      await onSubmit({
        service: effectiveServiceId, // Child type ID when parent has children, else parent ID
        category_ids: categoryIds,
        description: description.trim(),
        devicesAffected,
        dispatchDate,
        dispatchTime,
        contactNumber: contactNumber.trim(),
        contactPerson: contactPerson.trim(),
        email: email.trim(),
        preferredContactDate,
        preferredContactTime,
        attachments,
        existingAttachments: existingAttachments.length > 0 ? existingAttachments : undefined,
        submitRequest,
      })
      onClose()
      resetForm()
    } catch {
      // On error, keep modal open so the user can fix and retry
    }
  }

  const ALLOWED_IMAGE_TYPES = ['image/jpeg', 'image/png', 'image/gif']

  function filterImageFiles(files: FileList | File[]): File[] {
    const list = Array.isArray(files) ? files : Array.from(files)
    return list.filter((f) => ALLOWED_IMAGE_TYPES.includes(f.type))
  }

  function onFilesSelected(files: FileList | null) {
    if (!files) return
    const filtered = filterImageFiles(files)
    setAttachments((prev) => [...prev, ...filtered])
    markTouched('attachments')
    
    // Note: Object URLs are created on-demand in the render for preview
    // They will be cleaned up when files are removed or component unmounts
  }

  function onDragOver(e: React.DragEvent) {
    e.preventDefault()
    e.stopPropagation()
    setIsDragging(true)
  }

  function onDragLeave(e: React.DragEvent) {
    e.preventDefault()
    e.stopPropagation()
    setIsDragging(false)
  }

  function onDrop(e: React.DragEvent) {
    e.preventDefault()
    e.stopPropagation()
    setIsDragging(false)
    const files = e.dataTransfer?.files
    if (!files?.length) return
    const filtered = filterImageFiles(files)
    setAttachments((prev) => [...prev, ...filtered])
    markTouched('attachments')
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={() => {
        onClose()
        // resetForm() // optional
      }}
      size="2xl"
      isCentered
      blockScrollOnMount={true}
      scrollBehavior="inside"
      motionPreset="slideInBottom"
    >
      <ModalOverlay 
        bg="blackAlpha.600" 
        backdropFilter="blur(2px)"
      />

      <ModalContent
        borderRadius="md"
        maxH="90vh"
        position="relative"
        display="flex"
        flexDirection="column"
      >
        <ModalHeader py={3} flexShrink={0}>
          <HStack justify="space-between" align="center">
            <Text fontSize="md" fontWeight="700" color="red.700">
              {isEditMode ? 'Edit Ticket' : 'Create New Ticket'}
            </Text>

            <IconButton
              aria-label="Close"
              icon={<X size={18} />}
              variant="ghost"
              onClick={onClose}
            />
          </HStack>
        </ModalHeader>

        <ModalCloseButton display="none" />
        <Divider />

        {/* BODY: only as tall as content; scroll when content exceeds max height */}
        <ModalBody
          pt={4}
          pb={1}
          px={6}
          overflowY="auto"
          overflowX="hidden"
          flex="1 1 auto"
          minH={0}
          maxH="calc(90vh - 100px)"
          position="relative"
          sx={{
            '& > *': {
              position: 'relative',
            },
            '&::-webkit-scrollbar': { width: '8px' },
            '&::-webkit-scrollbar-thumb': {
              background: 'rgba(0,0,0,0.18)',
              borderRadius: '999px',
              border: '2px solid transparent',
              backgroundClip: 'content-box',
            },
            '&::-webkit-scrollbar-track': { background: 'transparent' },
          }}
        >
          {isLoadingEdit ? (
            <Box py={8} textAlign="center" color="gray.500">
              Loading request...
            </Box>
          ) : (
          <VStack align="stretch" spacing={3} flex="0 0 auto" pb={0} mb={0}>
            {/* Request Details */}
            <Box>
              <Text fontWeight="700" fontSize="sm" mb={2} color="blue.900">
                Request Details
              </Text>

              <Grid templateColumns={{ base: '1fr', md: 'repeat(2, 1fr)' }} gap={3} rowGap={3}>
                {/* Service (Parent) - full width */}
                <GridItem colSpan={{ base: 1, md: 2 }}>
                  <FormControl
                    isInvalid={!!errors.serviceParent && touched.serviceParent}
                    isRequired
                  >
                    <FormLabel fontSize="sm">Service</FormLabel>
                    <Select
                      value={serviceParent}
                      onChange={(e) => setServiceParent(e.target.value)}
                      onBlur={() => markTouched('serviceParent')}
                      placeholder="Select service"
                    >
                      {serviceParents.map((o) => (
                        <option key={o.value} value={o.value}>
                          {o.label}
                        </option>
                      ))}
                    </Select>
                    <FormErrorMessage>{errors.serviceParent}</FormErrorMessage>
                  </FormControl>
                </GridItem>

                {/* Type of Service (Child) - next row, full width when parent has children */}
                {hasChildTypes && (
                  <GridItem colSpan={{ base: 1, md: 2 }}>
                    <FormControl
                      isInvalid={!!errors.serviceType && touched.serviceType}
                      isRequired
                    >
                      <FormLabel fontSize="sm">Type of Service</FormLabel>
                      <Select
                        value={serviceType}
                        onChange={(e) => setServiceType(e.target.value)}
                        onBlur={() => markTouched('serviceType')}
                        placeholder="Select type"
                        isDisabled={!serviceParent}
                      >
                        {filteredServiceTypes.map((o) => (
                          <option key={o.value} value={o.value}>
                            {o.label}
                          </option>
                        ))}
                      </Select>
                      <FormErrorMessage>{errors.serviceType}</FormErrorMessage>
                    </FormControl>
                  </GridItem>
                )}

                {/* Categories - next line, full width, not required */}
                <GridItem colSpan={{ base: 1, md: 2 }}>
                  <FormControl
                    isInvalid={!!errors.category && touched.category}
                  >
                    <FormLabel fontSize="sm">Categories</FormLabel>
                    <Popover placement="bottom-start" isLazy strategy="fixed">
                      <PopoverTrigger>
                        <Button
                          type="button"
                          variant="outline"
                          w="100%"
                          minH="40px"
                          justifyContent="space-between"
                          fontWeight="normal"
                          textAlign="left"
                          onBlur={() => markTouched('category')}
                        >
                          {categoryIds.length
                            ? `${categoryIds.length} selected`
                            : 'Select categories'}
                        </Button>
                      </PopoverTrigger>
                      <Portal appendToParentPortal={false}>
                        <PopoverContent w="auto" minW="220px" maxW="320px" maxH="280px" overflowY="auto" zIndex={1500}>
                        <PopoverBody py={2} px={2}>
                          <CheckboxGroup
                            value={categoryIds}
                            onChange={(v) => setCategoryIds((v as string[]) || [])}
                          >
                            <VStack align="stretch" spacing={2}>
                              {(categoryOptions ?? []).map((o) => (
                                <Checkbox key={o.value} value={o.value} size="sm">
                                  {o.label}
                                </Checkbox>
                              ))}
                            </VStack>
                          </CheckboxGroup>
                        </PopoverBody>
                      </PopoverContent>
                      </Portal>
                    </Popover>
                    {categoryIds.length > 0 && (
                      <Wrap mt={2} spacing={1}>
                        {categoryIds.map((id) => {
                          const opt = (categoryOptions ?? []).find((o) => o.value === id)
                          return (
                            <WrapItem key={id}>
                              <Box
                                as="span"
                                fontSize="xs"
                                px={2}
                                py={0.5}
                                borderRadius="md"
                                bg="gray.100"
                                color="gray.700"
                              >
                                {opt?.label ?? id}
                              </Box>
                            </WrapItem>
                          )
                        })}
                      </Wrap>
                    )}
                    <FormErrorMessage>{errors.category}</FormErrorMessage>
                  </FormControl>
                </GridItem>

                {/* Issue Description - next line */}
                <GridItem colSpan={{ base: 1, md: 2 }}>
                  <FormControl
                    isInvalid={!!errors.description && touched.description}
                    isRequired
                  >
                    <FormLabel fontSize="sm">Issue Description</FormLabel>
                    <Textarea
                      value={description}
                      onChange={(e) => setDescription(e.target.value)}
                      onBlur={() => markTouched('description')}
                      placeholder="e.g., Dirty printout with line / Paper jam on Tray 1 / Printer not connecting to network"
                      minH="100px"
                      resize="none"
                    />
                    <FormErrorMessage>{errors.description}</FormErrorMessage>
                  </FormControl>
                </GridItem>

                <GridItem>
                  <FormControl
                    isInvalid={
                      !!errors.devicesAffected && touched.devicesAffected
                    }
                  >
                    <FormLabel fontSize="sm">
                      Count of devices affected
                    </FormLabel>
                    <NumberInput
                      value={devicesAffected}
                      min={1}
                      onChange={(_, n) =>
                        setDevicesAffected(Number.isNaN(n) ? 1 : n)
                      }
                      onBlur={() => markTouched('devicesAffected')}
                    >
                      <NumberInputField />
                      <NumberInputStepper>
                        <NumberIncrementStepper />
                        <NumberDecrementStepper />
                      </NumberInputStepper>
                    </NumberInput>
                    <FormErrorMessage>
                      {errors.devicesAffected}
                    </FormErrorMessage>
                  </FormControl>
                </GridItem>

                <GridItem>
                  <FormControl>
                    <FormLabel fontSize="sm">Preferred date and time of dispatch</FormLabel>
                    <DateTimePickerPopover
                      value={dispatchDT}
                      onChange={(next) => {
                        setDispatchDT(next)
                        if (next.date) setDispatchDate(next.date.toISOString().slice(0, 10))
                        if (next.time) setDispatchTime(next.time)
                        markTouched('dispatchDate')
                        markTouched('dispatchTime')
                      }}
                    />
                  </FormControl>
                </GridItem>

                <GridItem colSpan={{ base: 1, md: 2 }}>
                  <FormControl
                    isInvalid={!!errors.attachments && touched.attachments}
                  >
                    <FormLabel fontSize="sm">
                      Attach Images
                    </FormLabel>

                    <Box
                      border="2px dashed"
                      borderColor={isDragging ? 'blue.400' : 'gray.300'}
                      borderRadius="md"
                      p={4}
                      textAlign="center"
                      bg={isDragging ? 'blue.50' : 'transparent'}
                      transition="background 0.2s, border-color 0.2s"
                      onDragOver={onDragOver}
                      onDragLeave={onDragLeave}
                      onDrop={onDrop}
                    >
                      <VStack spacing={2}>
                        <Text fontSize="sm" color="gray.500">
                          Drag and drop image files here, or click to browse
                        </Text>
                        <Text fontSize="xs" color="gray.400">
                          Supported formats: JPG, PNG, GIF (max 5 MB each)
                        </Text>

                        <Input
                          type="file"
                          accept="image/jpeg,image/png,image/gif"
                          multiple
                          onChange={(e) => onFilesSelected(e.target.files)}
                          display="none"
                          id="client-ticket-attachments"
                        />

                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() =>
                            document
                              .getElementById('client-ticket-attachments')
                              ?.click()
                          }
                        >
                          Browse files
                        </Button>

                        {/* Preview newly selected files */}
                        {attachments.length > 0 && (
                          <Box pt={3} w="100%">
                            <Text fontSize="xs" fontWeight="600" color="gray.700" mb={2}>
                              New Files ({attachments.length}):
                            </Text>
                            <Wrap spacing={2}>
                              {attachments.map((file, index) => {
                                const isImage = file.type.startsWith('image/')
                                // Find corresponding preview URL for this file
                                const imageFiles = attachments.filter(f => f.type.startsWith('image/'))
                                const imageIndex = imageFiles.findIndex(f => f === file)
                                const previewUrl = isImage && imageIndex >= 0 ? previewUrls[imageIndex] : null
                                
                                return (
                                  <WrapItem key={index}>
                                    <Box
                                      position="relative"
                                      w="80px"
                                      h="80px"
                                      borderRadius="md"
                                      overflow="hidden"
                                      border="1px solid"
                                      borderColor="gray.300"
                                      bg="gray.100"
                                    >
                                      {isImage && previewUrl ? (
                                        <>
                                          <Box
                                            as="img"
                                            src={previewUrl}
                                            alt={file.name}
                                            w="100%"
                                            h="100%"
                                            objectFit="cover"
                                          />
                                          <IconButton
                                            aria-label="Remove file"
                                            icon={<X size={14} />}
                                            size="xs"
                                            position="absolute"
                                            top={1}
                                            right={1}
                                            bg="red.500"
                                            color="white"
                                            _hover={{ bg: 'red.600' }}
                                            onClick={() => {
                                              setAttachments(prev => prev.filter((_, i) => i !== index))
                                            }}
                                          />
                                        </>
                                      ) : (
                                        <VStack
                                          h="100%"
                                          justify="center"
                                          spacing={0}
                                          p={1}
                                          bg="blue.50"
                                          borderColor="blue.200"
                                        >
                                          <Text fontSize="xs" color="blue.600" fontWeight="600" textAlign="center">
                                            FILE
                                          </Text>
                                          <IconButton
                                            aria-label="Remove file"
                                            icon={<X size={12} />}
                                            size="xs"
                                            position="absolute"
                                            top={1}
                                            right={1}
                                            bg="red.500"
                                            color="white"
                                            _hover={{ bg: 'red.600' }}
                                            onClick={() => {
                                              setAttachments(prev => prev.filter((_, i) => i !== index))
                                            }}
                                          />
                                        </VStack>
                                      )}
                                    </Box>
                                  </WrapItem>
                                )
                              })}
                            </Wrap>
                          </Box>
                        )}
                        
                        {existingAttachments.length > 0 && attachments.length === 0 && (
                          <Box pt={2} w="100%">
                            <Text fontSize="xs" color="gray.600">
                              {existingAttachments.length} existing file(s)
                            </Text>
                          </Box>
                        )}
                      </VStack>
                    </Box>

                    {/* Display existing attachments when editing */}
                    {existingAttachments.length > 0 && (
                      <Box mt={3}>
                        <Text fontSize="xs" fontWeight="600" color="gray.700" mb={2}>
                          Existing Attachments:
                        </Text>
                        <VStack align="stretch" spacing={2}>
                          {existingAttachments.map((attachment, index) => {
                            const fileUrl = attachment.file_url || attachment.path
                            const isImage = attachment.file_name?.match(/\.(jpg|jpeg|png|gif|webp)$/i)
                            
                            return (
                              <HStack
                                key={index}
                                p={2}
                                bg="gray.50"
                                borderRadius="md"
                                border="1px solid"
                                borderColor="gray.200"
                                spacing={3}
                                align="center"
                              >
                                {/* Thumbnail Preview */}
                                {isImage && fileUrl ? (
                                  <Box
                                    w="60px"
                                    h="60px"
                                    borderRadius="md"
                                    overflow="hidden"
                                    flexShrink={0}
                                    border="1px solid"
                                    borderColor="gray.300"
                                    bg="gray.100"
                                    cursor="pointer"
                                    onClick={() => fileUrl && window.open(fileUrl, '_blank')}
                                    _hover={{ opacity: 0.8 }}
                                  >
                                    <Box
                                      as="img"
                                      src={fileUrl}
                                      alt={attachment.file_name}
                                      w="100%"
                                      h="100%"
                                      objectFit="cover"
                                    />
                                  </Box>
                                ) : (
                                  <Box
                                    w="60px"
                                    h="60px"
                                    borderRadius="md"
                                    bg="blue.50"
                                    border="1px solid"
                                    borderColor="blue.200"
                                    display="flex"
                                    alignItems="center"
                                    justifyContent="center"
                                    flexShrink={0}
                                  >
                                    <Text fontSize="xs" color="blue.600" fontWeight="600">
                                      FILE
                                    </Text>
                                  </Box>
                                )}
                                
                                {/* File Info */}
                                <Box flex="1" minW={0}>
                                  <Text fontSize="sm" color="gray.700" fontWeight="500" isTruncated>
                                    {attachment.file_name}
                                  </Text>
                                  {isImage && (
                                    <Text fontSize="xs" color="gray.500">
                                      Image
                                    </Text>
                                  )}
                                </Box>
                                
                                {/* Actions */}
                                <HStack spacing={1}>
                                  {fileUrl && (
                                    <IconButton
                                      aria-label="View file"
                                      icon={<ExternalLink size={16} />}
                                      size="sm"
                                      variant="ghost"
                                      onClick={() => window.open(fileUrl, '_blank')}
                                    />
                                  )}
                                  <IconButton
                                    aria-label="Remove file"
                                    icon={<Trash2 size={16} />}
                                    size="sm"
                                    variant="ghost"
                                    colorScheme="red"
                                    onClick={() => {
                                      setExistingAttachments(prev => prev.filter((_, i) => i !== index))
                                    }}
                                  />
                                </HStack>
                              </HStack>
                            )
                          })}
                        </VStack>
                      </Box>
                    )}

                    <FormErrorMessage>{errors.attachments}</FormErrorMessage>
                  </FormControl>
                </GridItem>
              </Grid>
            </Box>

            <Divider my={2} />

            {/* Contact Details */}
            <Box>
              <Text fontWeight="700" fontSize="sm" mb={2} color="blue.900">
                Contact Details
              </Text>

              <Grid templateColumns="repeat(2, 1fr)" gap={3} rowGap={3}>
                <GridItem>
                  <FormControl
                    isInvalid={!!errors.contactNumber && touched.contactNumber}
                    isRequired
                  >
                    <FormLabel fontSize="sm">Contact Number</FormLabel>
                    <Input
                      value={contactNumber}
                      onChange={(e) => setContactNumber(e.target.value)}
                      onBlur={() => markTouched('contactNumber')}
                      placeholder="e.g., 09xxxxxxxxx"
                    />
                    <FormErrorMessage>{errors.contactNumber}</FormErrorMessage>
                  </FormControl>
                </GridItem>

                <GridItem>
                  <FormControl
                    isInvalid={!!errors.contactPerson && touched.contactPerson}
                    isRequired
                  >
                    <FormLabel fontSize="sm">Contact Person</FormLabel>
                    <Input
                      value={contactPerson}
                      onChange={(e) => setContactPerson(e.target.value)}
                      onBlur={() => markTouched('contactPerson')}
                      placeholder="e.g., Juan Dela Cruz"
                    />
                    <FormErrorMessage>{errors.contactPerson}</FormErrorMessage>
                  </FormControl>
                </GridItem>

                <GridItem>
                  <FormControl
                    isInvalid={!!errors.email && touched.email}
                    isRequired
                  >
                    <FormLabel fontSize="sm">Email Address</FormLabel>
                    <Input
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      onBlur={() => markTouched('email')}
                      placeholder="e.g., name@company.com"
                    />
                    <FormErrorMessage>{errors.email}</FormErrorMessage>
                  </FormControl>
                </GridItem>

                <GridItem>
                  <FormControl>
                    <FormLabel fontSize="sm">
                      Preferred contact date and time
                    </FormLabel>

                    <DateTimePickerPopover
                      value={contactDate}
                      onChange={(next) => {
                        setContactDate(next)
                        if (next.date)
                          setPreferredContactDate(next.date.toISOString().slice(0, 10))
                        if (next.time)
                          setPreferredContactTime(next.time)
                        markTouched('preferredContactDate')
                        markTouched('preferredContactTime')
                      }}
                    />

                  </FormControl>
                </GridItem>
              </Grid>
            </Box>
          </VStack>
          )}
        </ModalBody>

        <ModalFooter
          flexShrink={0}
          borderTop="1px solid"
          borderColor="gray.200"
          bg="white"
          py={3}
        >
          <HStack justify="flex-end" spacing={3} w="100%">
            <Button variant="ghost" onClick={onClose}>
              Cancel
            </Button>
            <Button
              variant="outline"
              onClick={() => handleSubmit(false)}
              isLoading={isSubmitting}
              loadingText="Saving..."
              isDisabled={hasErrors}
            >
              Save as Draft
            </Button>
            <Button
              bg="red.700"
              _hover={{ bg: 'red.800' }}
              color="white"
              onClick={() => handleSubmit(true)}
              isLoading={isSubmitting}
              loadingText={isEditMode ? 'Submitting...' : 'Creating...'}
              isDisabled={hasErrors}
            >
              {isEditMode ? 'Submit Request' : 'Create Ticket'}
            </Button>
          </HStack>
        </ModalFooter>
      </ModalContent>
    </Modal>
  )
}

export default CreateTicketModal
